import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import * as marketplaceAuthService from '../../services/marketplaceAuthService';
import { ROLES } from '../../firebase.marketplace';
import { SEOHead } from '../../components/SEO';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Divider, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  Avatar,
  CircularProgress,
  Alert,
  Chip,
  Tooltip
} from '@mui/material';
import { 
  Security as SecurityIcon,
  DevicesOther as DevicesIcon,
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  History as HistoryIcon,
  Edit as EditIcon,
  VpnKey as KeyIcon,
  VerifiedUser as VerifiedIcon,
  SupervisorAccount as AdminIcon,
  MusicNote as ArtistIcon,
  Person as UserIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';

// Styled components
const Container = styled(Box)`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  background: linear-gradient(180deg, #030617 0%, #0a1128 100%);
  padding: 2rem 1rem;
`;

const ProfileContainer = styled(Paper)`
  width: 100%;
  max-width: 1200px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  padding: 2rem;
  color: white;
`;

const ProfileHeader = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 600px) {
    flex-direction: column;
    text-align: center;
  }
`;

const ProfileAvatar = styled(Avatar)`
  width: 100px;
  height: 100px;
  margin-right: 2rem;
  background-color: #4a90e2;
  
  @media (max-width: 600px) {
    margin-right: 0;
    margin-bottom: 1rem;
  }
`;

const RoleBadge = styled(Chip)`
  margin: 0.5rem 0.5rem 0 0;
`;

const FeatureCard = styled(Card)`
  height: 100%;
  background: rgba(255, 255, 255, 0.08);
  color: white;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const CardTitle = styled(Typography)`
  display: flex;
  align-items: center;
  
  & svg {
    margin-right: 0.5rem;
  }
`;

const UserProfile = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRoles, setUserRoles] = useState({});
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [activeSessions, setActiveSessions] = useState(0);
  const [orderCount, setOrderCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser) {
        navigate('/marketplace/auth/signin');
        return;
      }
      
      try {
        // Fetch user roles
        const roles = await marketplaceAuthService.getUserRoles(currentUser.uid);
        setUserRoles(roles);
        
        // Check if MFA is enabled
        const userDoc = await marketplaceAuthService.getUserDocument(currentUser.uid);
        setMfaEnabled(userDoc?.mfaEnrolled || false);
        
        // Get active sessions
        const sessions = await marketplaceAuthService.getUserSessions(currentUser.uid);
        setActiveSessions(sessions.length);
        
        // Fetch order count (placeholder - implement based on your order structure)
        setOrderCount(0);
        
        // Fetch wishlist count (placeholder - implement based on your wishlist structure)
        setWishlistCount(0);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user profile data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [currentUser, navigate]);
  
  // Helper function to get role display info
  const getRoleInfo = (role) => {
    switch (role) {
      case ROLES.SUPERADMIN:
        return { 
          label: 'Super Admin', 
          icon: <AdminIcon />, 
          color: 'secondary',
          description: 'Full system access including user management, content moderation, and system settings'
        };
      case ROLES.ADMIN:
        return { 
          label: 'Admin', 
          icon: <AdminIcon />, 
          color: 'error',
          description: 'Manage artists, content, and marketplace operations'
        };
      case ROLES.ARTIST:
        return { 
          label: 'Artist', 
          icon: <ArtistIcon />, 
          color: 'success',
          description: 'Upload and manage your music and content'
        };
      case ROLES.USER:
        return { 
          label: 'User', 
          icon: <UserIcon />, 
          color: 'primary',
          description: 'Standard user privileges'
        };
      default:
        return { 
          label: role, 
          icon: <VerifiedIcon />, 
          color: 'default',
          description: 'Custom role'
        };
    }
  };
  
  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  return (
    <Container>
      <SEOHead
        title="My Profile | Konzo Marketplace"
        description="Manage your Konzo Marketplace profile, security settings, and account preferences."
        keywords="profile, account, security, konzo vibez, marketplace"
        canonicalUrl="/marketplace/profile"
      />
      
      <ProfileContainer elevation={3}>
        {error && (
          <Alert 
            severity="error" 
            style={{ marginBottom: '1.5rem', backgroundColor: 'rgba(211, 47, 47, 0.1)', color: '#f44336' }}
          >
            {error}
          </Alert>
        )}
        
        <ProfileHeader>
          <ProfileAvatar src={currentUser.photoURL} alt={currentUser.displayName || 'User'}>
            {!currentUser.photoURL && (currentUser.displayName?.[0] || currentUser.email?.[0] || 'U')}
          </ProfileAvatar>
          
          <Box flex="1">
            <Typography variant="h4" gutterBottom>
              {currentUser.displayName || 'User Profile'}
            </Typography>
            
            <Typography variant="body1" color="rgba(255, 255, 255, 0.7)" gutterBottom>
              {currentUser.email}
            </Typography>
            
            <Box>
              {Object.keys(userRoles).map(role => 
                userRoles[role] && (
                  <Tooltip key={role} title={getRoleInfo(role).description} arrow>
                    <RoleBadge 
                      label={getRoleInfo(role).label}
                      color={getRoleInfo(role).color}
                      icon={getRoleInfo(role).icon}
                    />
                  </Tooltip>
                )
              )}
            </Box>
          </Box>
          
          <Button 
            variant="outlined" 
            startIcon={<EditIcon />}
            style={{ color: 'white', borderColor: 'white' }}
            onClick={() => alert('Edit profile functionality coming soon!')}
          >
            Edit Profile
          </Button>
        </ProfileHeader>
        
        <Divider style={{ margin: '1.5rem 0', backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
        
        <Typography variant="h5" gutterBottom>
          Account & Security
        </Typography>
        
        <Grid container spacing={3} style={{ marginBottom: '2rem' }}>
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard>
              <CardContent>
                <CardTitle variant="h6">
                  <SecurityIcon />
                  Two-Factor Authentication
                </CardTitle>
                <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" style={{ marginTop: '1rem' }}>
                  {mfaEnabled 
                    ? 'Your account is protected with two-factor authentication.' 
                    : 'Add an extra layer of security to your account.'}
                </Typography>
                <Box mt={2} display="flex" alignItems="center">
                  <Chip 
                    label={mfaEnabled ? 'Enabled' : 'Disabled'} 
                    color={mfaEnabled ? 'success' : 'default'}
                    size="small"
                    icon={mfaEnabled ? <VerifiedIcon /> : <KeyIcon />}
                  />
                </Box>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  color={mfaEnabled ? 'error' : 'primary'}
                  onClick={() => navigate('/marketplace/security/mfa-setup')}
                >
                  {mfaEnabled ? 'Manage 2FA' : 'Enable 2FA'}
                </Button>
              </CardActions>
            </FeatureCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard>
              <CardContent>
                <CardTitle variant="h6">
                  <DevicesIcon />
                  Active Sessions
                </CardTitle>
                <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" style={{ marginTop: '1rem' }}>
                  Manage devices that are currently signed in to your account.
                </Typography>
                <Box mt={2}>
                  <Chip 
                    label={`${activeSessions} active ${activeSessions === 1 ? 'session' : 'sessions'}`} 
                    color="primary"
                    size="small"
                  />
                </Box>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  color="primary"
                  onClick={() => navigate('/marketplace/security/sessions')}
                >
                  Manage Sessions
                </Button>
              </CardActions>
            </FeatureCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard>
              <CardContent>
                <CardTitle variant="h6">
                  <KeyIcon />
                  Password & Authentication
                </CardTitle>
                <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" style={{ marginTop: '1rem' }}>
                  Update your password and manage authentication methods.
                </Typography>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  color="primary"
                  onClick={() => alert('Password management coming soon!')}
                >
                  Change Password
                </Button>
              </CardActions>
            </FeatureCard>
          </Grid>
        </Grid>
        
        <Typography variant="h5" gutterBottom>
          My Marketplace
        </Typography>
        
        <Grid container spacing={3}>
          {/* Payment API Credentials Card - Only visible to artists */}
          {userRoles[ROLES.ARTIST] && (
            <Grid item xs={12} sm={6} md={4}>
              <FeatureCard>
                <CardContent>
                  <CardTitle variant="h6">
                    <PaymentIcon />
                    Payment API Credentials
                  </CardTitle>
                  <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" style={{ marginTop: '1rem' }}>
                    Manage your payment API credentials for processing sales.
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    color="primary"
                    onClick={() => navigate('/marketplace/payment-credentials')}
                  >
                    Manage Credentials
                  </Button>
                </CardActions>
              </FeatureCard>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard>
              <CardContent>
                <CardTitle variant="h6">
                  <HistoryIcon />
                  Order History
                </CardTitle>
                <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" style={{ marginTop: '1rem' }}>
                  View your past orders and purchases.
                </Typography>
                <Box mt={2}>
                  <Chip 
                    label={`${orderCount} ${orderCount === 1 ? 'order' : 'orders'}`} 
                    color="primary"
                    size="small"
                  />
                </Box>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  color="primary"
                  onClick={() => navigate('/marketplace/orders')}
                >
                  View Orders
                </Button>
              </CardActions>
            </FeatureCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard>
              <CardContent>
                <CardTitle variant="h6">
                  <FavoriteIcon />
                  Wishlist
                </CardTitle>
                <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" style={{ marginTop: '1rem' }}>
                  Manage your saved items and wishlist.
                </Typography>
                <Box mt={2}>
                  <Chip 
                    label={`${wishlistCount} ${wishlistCount === 1 ? 'item' : 'items'}`} 
                    color="primary"
                    size="small"
                  />
                </Box>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  color="primary"
                  onClick={() => navigate('/marketplace/wishlist')}
                >
                  View Wishlist
                </Button>
              </CardActions>
            </FeatureCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <FeatureCard>
              <CardContent>
                <CardTitle variant="h6">
                  <CartIcon />
                  Shopping Cart
                </CardTitle>
                <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" style={{ marginTop: '1rem' }}>
                  View and manage items in your shopping cart.
                </Typography>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  color="primary"
                  onClick={() => navigate('/marketplace/cart')}
                >
                  View Cart
                </Button>
              </CardActions>
            </FeatureCard>
          </Grid>
        </Grid>
        
        {/* Admin/Artist specific sections would go here */}
        {(userRoles[ROLES.ADMIN] || userRoles[ROLES.ARTIST]) && (
          <>
            <Divider style={{ margin: '2rem 0', backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
            
            <Typography variant="h5" gutterBottom>
              {userRoles[ROLES.ADMIN] ? 'Admin Tools' : 'Artist Tools'}
            </Typography>
            
            <Grid container spacing={3}>
              {userRoles[ROLES.ADMIN] && (
                <Grid item xs={12} sm={6} md={4}>
                  <FeatureCard>
                    <CardContent>
                      <CardTitle variant="h6">
                        <AdminIcon />
                        Admin Panel
                      </CardTitle>
                      <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" style={{ marginTop: '1rem' }}>
                        Access administrative tools and settings.
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button 
                        size="small" 
                        color="primary"
                        onClick={() => navigate('/admin-panel')}
                      >
                        Go to Admin Panel
                      </Button>
                    </CardActions>
                  </FeatureCard>
                </Grid>
              )}
              
              {userRoles[ROLES.ARTIST] && (
                <Grid item xs={12} sm={6} md={4}>
                  <FeatureCard>
                    <CardContent>
                      <CardTitle variant="h6">
                        <ArtistIcon />
                        Artist Dashboard
                      </CardTitle>
                      <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" style={{ marginTop: '1rem' }}>
                        Manage your music, tracks, and artist profile.
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button 
                        size="small" 
                        color="primary"
                        onClick={() => navigate('/artist-dashboard')}
                      >
                        Go to Artist Dashboard
                      </Button>
                    </CardActions>
                  </FeatureCard>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </ProfileContainer>
    </Container>
  );
};

export default UserProfile;
